"use client";

import { useTranslations } from "next-intl";

type AgentStatus = "online" | "offline" | "form";
type AgentConnectionMode = "realtime" | "polling" | null;

/**
 * 원래는 online/offline/unverified/unregistered 4단이었다 — 웹소켓 실시간 연결이 생기면서
 * 참여사가 웹훅을 아예 등록 안 해도 정상 동작하는 게 기본 경로가 됐고, "웹훅 미등록"과 "웹훅
 * 등록했지만 미검증"을 구분하는 게 참여사에게 실질적으로 다른 의미를 못 주는 죽은 구분이 됐다
 * (사용자 판단) — online/offline 2단으로 단순화한다.
 *
 * "form"(0031, A2F 리스팅)은 에이전트 연결 상태가 아니라 "AI 에이전트가 직접 응대하지 않고
 * 문의 폼으로 연결된다"는 종류 표시다 — 대표 지시로 online(초록)/offline(빨강)과 같은 자리에
 * 주황으로 은근하게 얹는다(별도 UI를 새로 만들지 않고 기존 뱃지 자리를 그대로 재사용).
 *
 * 2026-09-15: online은 이제 실시간(웹소켓/웹훅)과 폴링(list_pending_events, 10분 TTL) 두 경로로
 * 올 수 있다(agent-status.ts의 AgentConnectionMode) — 상태 자체는 둘 다 "online"으로 같지만,
 * "지금 당장 푸시 받을 수 있는지"는 실질적으로 다른 정보라 뭉개지 않고 점 스타일(채움=실시간,
 * 테두리만=폴링)로만 은근하게 구분한다(대표 지시: 별도 뱃지보다 기존 자리에 얹는 쪽으로).
 */
export default function AgentStatusBadge({ status, connection }: { status: AgentStatus; connection?: AgentConnectionMode }) {
  const t = useTranslations("agentStatus");

  const styles: Record<AgentStatus, string> = {
    online: "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300",
    offline: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
    form: "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300",
  };

  const isPolling = status === "online" && connection === "polling";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${styles[status]}`}
      title={isPolling ? t("connectionPolling") : undefined}
    >
      <span className={isPolling ? "h-1.5 w-1.5 rounded-full border border-current" : "h-1.5 w-1.5 rounded-full bg-current"} />
      {t(status)}
    </span>
  );
}
